/* eslint-disable ember/no-computed-properties-in-native-classes, no-restricted-properties, warp-drive/no-legacy-request-patterns */
// Extends from
import Controller, { inject as controller } from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember imports
import moment from 'moment';
import { isArray } from '@ember/array';
import { alias } from '@ember/object/computed';
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal imports
import ApplicationStatus from 'linced-app/enums/application-status';
import ContactType from 'linced-app/enums/contact-type';
import { labelForOption } from 'linced-app/helpers/label-for-option';
// External imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import {
  filterBy,
  findBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class ApplicationsController extends Controller {
  // Services
  @service
  personOptions;

  @service
  toaster;

  ApplicationStatus = ApplicationStatus;
  @controller('application')
  appController;
  @tracked
  status = ApplicationStatus.UNPROCESSED;
  queryParams = ['status'];

  @alias('appController.applicationFormURL')
  applicationFormURL;

  @computed(
    'model.applications.@each.{status,personId}',
    'selectedCustomFields.@each.{section,field}',
    'filters.@each.{field,value}',
  )
  get applications() {
    let applications = this.model.applications;
    if (isEmpty(applications)) {
      return [];
    }
    applications = this.applyFilters(applications);
    // addCustomFieldsData mutates the original applications
    this.addCustomFieldsData(applications);

    return applications;
  }

  applyFilters(applications) {
    if (!isEmpty(this.filters)) {
      // Apply all filters to each row in one hit, to avoid iterating over a (potentially) large dataset multiple times.
      return applications.filter((application) => {
        return this.filters.every((filter) => {
          let dataType;
          switch (filter.field) {
            case 'currentYearLevel':
              dataType = DataType.INTEGER;
              break;
            default:
              dataType = DataType.STRING;
              break;
          }
          return filterValue(
            get(application, filter.field) ?? '',
            dataType,
            filter.value,
          );
        });
      });
    }
    return applications;
  }

  addCustomFieldsData(applications) {
    // Add custom fields' data to each application
    let customFieldsSelected = this.selectedCustomFields.some(
      (object) => Object.keys(object).length !== 0,
    );
    if (customFieldsSelected) {
      this.getApplicationsCustomFields(applications, this.selectedCustomFields);
    }
  }

  // Custom columns setup
  // schema is the school's enrolment schema used in forms
  schema = null;

  // selectedCustomFields is the array with a pre-defined length, containing objects of sections and fields
  // per each column with custom fields a user can select
  @tracked
  selectedCustomFields = Array.from({ length: 3 }, () => ({}));

  // selectedFieldsPerSection groups all selected fields under each section
  // for the ease of excluding selected fields from the list of available ones.
  // It is a native getter rather than a computed. selectedCustomFields holds
  // plain objects whose `section` is a nested POJO and Ember does not track
  // POJO mutation.
  get selectedFieldsPerSection() {
    return this.selectedCustomFields.reduce((array, { section, field }) => {
      if (section && field) {
        if (!array[section.title]) {
          array[section.title] = [];
        }
        array[section.title].push(field);
      }
      return array;
    }, {});
  }

  // allSections is all the sections used in the school's schema
  allSections = [];

  // defaultFieldsKeys stores keys of fields that are already included in the table
  defaultFieldsKeys = Object.freeze([
    'core:currentYearLevel',
    'core:dob',
    'core:firstNameLegal',
    'core:firstNamePreferred',
    'core:intendedStart',
    'core:lastNameLegal',
    'core:lastNamePreferred',
    'corePrimary:intendedStart',
    'nzMOE:currentYearLevel',
  ]);

  // groupedFields is a multi-level list of fields under each section's title
  // to be used in the template
  @computed(
    'allSections.[]',
    'selectedFieldsPerSection.@each.{section,field}',
    'defaultFieldsKeys.[]',
  )
  get groupedFields() {
    return this.allSections.map((section) => {
      // Check if any fields from this section are already selected
      let selectedFields = this.selectedFieldsPerSection[section.title] || [];

      let options = section.fields.filter((field) => {
        // Exclude fields already present in the table
        // Exclude already selected fields from the list of all
        // Exclude "fields" that contain no keys but are rather used for descriptions
        return (
          !selectedFields.includes(field) &&
          !this.defaultFieldsKeys.includes(field.key) &&
          !isEmpty(field.key)
        );
      });
      return {
        groupName: section.title,
        options: [...options],
      };
    });
  }

  // getFieldData gets field data for an application from the appropriate attribute
  // given the schema section title
  getFieldData(application, sectionName, fieldKey) {
    let items;
    switch (sectionName) {
      case 'primary':
        items = filterBy(application.contacts, 'type', ContactType.PRIMARY);
        break;
      default:
        items = null;
        break;
    }

    if (isEmpty(items)) return '';

    return items
      .map(
        (item) =>
          (item.fields &&
            item.fields.get(fieldKey) &&
            item.fields.get(`${fieldKey}.value`)) ||
          '',
      )
      .filter((value) => value !== '')
      .join(', ');
  }

  // getLearnerFieldData gets data for an application from learners fields,
  // let it be an array of enums or single-valued fields
  getLearnerFieldData(application, customField) {
    let field = application.fields.get(customField.field.key);
    let values = field ? field.get('value') : '';

    if (isArray(values)) {
      // We're dealing with an array of probably enums, which requires us to get a label
      return values
        .toArray()
        .map((value) => labelForOption([customField.field, value]))
        .join(', ');
    }

    // Check for options for the schema's field as that will tell us whether it's an enum or not
    if (customField.field.optionsFromEnum || customField.field.options) {
      // It's a field that gets its labels from an enum or a list of options
      return labelForOption([customField.field, values]);
    }
    return values;
  }

  getSectionTitleToAttributeMap(schema) {
    return {
      [schema.primaryContacts.title]: 'primary',
    };
  }

  getApplicationsCustomFields(applications, customFields) {
    if (isEmpty(applications)) {
      return;
    }
    let sectionTitleToAttributeMap = this.getSectionTitleToAttributeMap(
      this.schema,
    );
    // Mutate the original array
    applications.forEach((application) => {
      customFields.forEach((customField, index) => {
        if (Object.keys(customField).length === 0) {
          // Skip an empty field
          return;
        }
        let attributeName = `customField${index + 1}`;
        let fieldData = sectionTitleToAttributeMap[customField.section.title]
          ? this.getFieldData(
              application,
              sectionTitleToAttributeMap[customField.section.title],
              customField.field.key,
            )
          : this.getLearnerFieldData(application, customField);

        application.set(attributeName, fieldData);
      });
    });
  }

  clearApplicationsCustomFields(applications, customFieldName) {
    applications = applications.forEach((application) => {
      application.set(customFieldName, null);
    });
  }

  updateCustomFieldObjectAtIndex(newObject, index) {
    let updatedFields = [
      ...this.selectedCustomFields.slice(0, index),
      newObject,
      ...this.selectedCustomFields.slice(index + 1),
    ];
    // set will ensure the applications are notified of the change
    this.selectedCustomFields = updatedFields;
  }

  // Filters setup
  fields = Object.freeze([
    'lastName',
    'firstName',
    'dob',
    'createDate',
    'intendedStart',
    'currentYearLevel',
    'customField1',
    'customField2',
    'customField3',
  ]);

  @tracked
  filters = [];

  // Selections setup
  @tracked
  selected = [];

  // clearSelection resets the selected
  clearSelection() {
    this.selected = [];
  }

  @action
  refresh() {
    this.appController.refreshUnprocessedApplications();
    this.model.applications.update();
  }

  @action
  changeStatusTab(status) {
    this.status = status;
    this.selected = [];
  }

  @action
  toggleCustomField(index, field) {
    if (isEmpty(field)) {
      // Remove the object associated with the field
      this.updateCustomFieldObjectAtIndex({}, index);
      // Remove the attribute associated with the field
      this.clearApplicationsCustomFields(
        this.applications,
        `customField${index + 1}`,
      );
      // Save the preferred fields for the user's view
      this.personOptions.setEnrolmentApplicationsFields(
        this.selectedCustomFields,
      );
      return;
    }
    // There is a little bit of reverse-engineering going on,
    // as PaperSelect is useless at telling us which groupName we've got the
    // option from
    this.allSections.forEach((section) => {
      if (section.fields.includes(field)) {
        this.updateCustomFieldObjectAtIndex({ section, field }, index);
      }
    });
    // Save the preferred fields for the user's view
    this.personOptions.setEnrolmentApplicationsFields(
      this.selectedCustomFields,
    );
  }

  // filter toggles a field to be filtered by
  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }
    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
      return;
    }
    // Existing filter has been changed; update it.
    set(filter, 'value', value);
  }

  @action
  reset() {
    this.filters = [];
  }

  // toggleAll selects or de-selects all rows in the table
  @action
  toggleAll() {
    if (this.selected.length >= this.applications.length) {
      // All or more are already selected, so
      // clear the selection.
      this.clearSelection();
      return;
    }
    // Not all selected; select them all.
    this.selected = [...this.applications];
  }

  // toggleSelected changes the state of a tick box for the selected row
  @action
  toggleSelected(application) {
    if (this.selected.includes(application)) {
      this.selected = this.selected.filter((a) => a !== application);
    } else {
      this.selected = [...this.selected, application];
    }
  }

  // moveToStatus moves selected applications to specified status tab
  @action
  moveToStatus(status) {
    let updates = this.selected.map((application) => {
      return application.reload().then((application) => {
        application.setProperties({
          updateTime: moment().unix(),
          status: status,
        });

        return application.save();
      });
    });
    return Promise.all(updates)
      .then(() => {
        this.toaster.success(
          'Successfully moved the selected Applications to the new status.',
        );
        // Refresh the count in the left menu.
        this.refresh();
        this.clearSelection();
      })
      .catch(() => {
        this.toaster.error(
          'Error moving the selected Applications to the new status.',
        );
      });
  }
}
